// What the chatbot can answer besides editing the product on screen: how-to
// questions answered from text held here, and questions answered by reading
// live data out of TPM.
//
// Matching is done locally rather than by the analysis service, because the
// natural language endpoint it exposes is not deployed on every environment -
// a processor asking "how do I override a license plate?" has to get an answer
// either way.

// A question answered from text, with no call to TPM.
export interface FaqEntry {
  id: string;
  // Shown on the quick-access chip and when the FAQ list is asked for.
  question: string;
  // Lower-case words that suggest this entry. Every word in a group has to be
  // present for the group to match, and any one group matching is enough, so
  // ['license', 'plate'] does not fire on the word "license" alone.
  keywords: string[][];
  answer: string;
  // When set, the entry is hidden from anyone without this permission.
  permission?: string;
}

// A question answered by reading live data out of TPM.
export type InfoQueryKind =
  | 'production-count'
  | 'current-license-plate'
  | 'license-plate-search'
  | 'product-details'
  | 'open-overrides';

export interface InfoQueryMatch {
  kind: InfoQueryKind;
  // The free-text argument, for a lookup that takes one.
  argument?: string;
}

export interface InfoQueryDefinition {
  kind: InfoQueryKind;
  question: string;
  keywords: string[][];
  permission?: string;
  // Set when the query needs a search term the processor has to supply.
  takesArgument?: boolean;
}

// Permission names are the ones already in use elsewhere in the app: the
// override menu item is gated on Item:ScanOverride, so anything that reads
// override or license plate data is gated the same way.
const OVERRIDE_PERMISSION = 'Item:ScanOverride';

export const INFO_QUERIES: InfoQueryDefinition[] = [
  {
    kind: 'production-count',
    question: "How many items have I produced today?",
    keywords: [
      ['today', 'order'],
      ['today', 'produce'],
      ['today', 'item'],
      ['today', 'count'],
      ['my', 'production'],
      ['how many', 'today'],
      ['production', 'count'],
      ['daily', 'total']
    ]
  },
  {
    kind: 'product-details',
    question: 'Show the details of the current product',
    keywords: [
      ['product', 'detail'],
      ['current', 'product'],
      ['this', 'item', 'detail'],
      ['what', 'scanned'],
      ['show', 'product']
    ]
  },
  {
    kind: 'current-license-plate',
    question: 'Which license plate am I working on?',
    keywords: [
      ['which', 'license', 'plate'],
      ['what', 'license', 'plate'],
      ['current', 'license', 'plate'],
      ['my', 'license', 'plate'],
      ['am i', 'override']
    ],
    permission: OVERRIDE_PERMISSION
  },
  {
    // Without a search term this lists what is there, so the plain
    // "show all license plates" is a first-class question rather than a
    // request for something to search by.
    kind: 'license-plate-search',
    question: 'Show all license plates',
    keywords: [
      ['license', 'plate'],
      ['find', 'license', 'plate'],
      ['search', 'license', 'plate'],
      ['look up', 'license', 'plate'],
      ['show', 'license', 'plate'],
      ['list', 'license', 'plate'],
      ['all', 'license', 'plate'],
      ['license', 'plate', 'info'],
      ['license', 'plate', 'detail']
    ],
    permission: OVERRIDE_PERMISSION,
    takesArgument: true
  },
  {
    kind: 'open-overrides',
    question: 'Show the overrides that are currently open',
    keywords: [
      ['show', 'override'],
      ['open', 'override'],
      ['current', 'override'],
      ['list', 'override'],
      ['active', 'override']
    ],
    permission: OVERRIDE_PERMISSION
  }
];

// Wording follows the labels on the screens themselves, so that a processor
// reading an answer can find the same words in the interface.
export const FAQ_ENTRIES: FaqEntry[] = [
  {
    id: 'override-license-plate',
    question: 'How do I override a license plate?',
    keywords: [
      ['how', 'override', 'license', 'plate'],
      ['how', 'assign', 'license', 'plate'],
      ['override', 'license', 'plate'],
      ['how', 'override']
    ],
    permission: OVERRIDE_PERMISSION,
    answer:
      'Overrides are set from the Override screen in the production menu, not from this chat.\n' +
      '1. Open Override from the production menu.\n' +
      '2. Under Override Options, either pick an existing one with Select Existing Override or set up a new one by choosing Select Store, Select Week Color and the Date.\n' +
      '3. Choose the plate under Select License Plate, or pick Empty License Plate and give it an LP Description and LP Location.\n' +
      '4. Save, and the header will show the override you are working under.\n' +
      'Once an override is active, everything you produce here - including items created from this chat - is recorded against that license plate. Ask "which license plate am I working on?" to check.'
  },
  {
    id: 'what-is-license-plate',
    question: 'What is a license plate?',
    keywords: [
      ['what', 'license', 'plate'],
      ['license', 'plate', 'mean'],
      ['define', 'license', 'plate']
    ],
    permission: OVERRIDE_PERMISSION,
    answer:
      'A license plate groups the items produced together so they can be moved and tracked as one unit. It carries a description, a location and the store it belongs to. While an override with a license plate is active, items you produce are added to that plate.'
  },
  {
    id: 'create-product',
    question: 'How do I create a product with the chatbot?',
    keywords: [
      ['how', 'create', 'product'],
      ['how', 'add', 'item'],
      ['how', 'record', 'video'],
      ['how', 'use', 'chatbot'],
      ['how', 'generate']
    ],
    answer:
      'Point the camera at the item and say or type "start recording", then "generate" when you are done.\n' +
      'The assistant identifies the product and shows a card with the department, category, size, quality and price.\n' +
      'Correct anything that is wrong with a command like "update category jeans" or by using the dropdowns on the card, then choose Approve to save and print the label.'
  },
  {
    id: 'fix-wrong-detail',
    question: 'The details are wrong - how do I correct them?',
    keywords: [
      ['wrong', 'detail'],
      ['how', 'correct'],
      ['how', 'change', 'detail'],
      ['how', 'fix'],
      ['how', 'edit', 'product']
    ],
    answer:
      'Type what you want changed, for example:\n' +
      '• "update department mens"\n' +
      '• "update category jeans"\n' +
      '• "update subcategory denim"\n' +
      '• "update size large"\n' +
      '• "update quality good"\n' +
      '• "update price 12.99"\n' +
      'You can also open the dropdowns on the product card. Changing the department or category clears the ones below it, so work top down.'
  },
  {
    id: 'price-wrong',
    question: 'Why is the price what it is?',
    keywords: [
      ['why', 'price'],
      ['price', 'wrong'],
      ['how', 'price', 'calculat'],
      ['where', 'price', 'from'],
      ['price', 'come from']
    ],
    answer:
      'The price comes from the price matrix your company has configured for the department and category, for the store group the item is being saved to - it is not invented by the assistant.\n' +
      'If it looks wrong, check that the department, category and subcategory on the card are right, since those decide which prices apply. You can always set it yourself with "update price 12.99".'
  },
  {
    id: 'print-label',
    question: 'The label did not print - what do I check?',
    keywords: [
      ['label', 'not', 'print'],
      ['printer', 'not', 'work'],
      ['print', 'problem'],
      ['printing', 'issue'],
      ['how', 'print']
    ],
    answer:
      'Labels print through QZ Tray, so first check that QZ Tray is running on this machine and that the browser prompt to allow it was accepted.\n' +
      'Then check that a printer is selected for your station. If the item was saved but the label did not come out, you can reprint it from the production history screen rather than producing the item again.'
  },
  {
    id: 'contact-support',
    question: 'I need help from a person',
    keywords: [
      ['talk', 'human'],
      ['talk', 'person'],
      ['contact', 'support'],
      ['need', 'help', 'person'],
      ['raise', 'ticket'],
      ['escalate']
    ],
    answer:
      'If something here cannot be resolved, contact your supervisor or the TPM support desk for your site, and include what you were doing and anything shown on the product card at the time.\n' +
      'Note down the department, category and price on the card before you leave this screen - it makes the problem much quicker to trace.'
  }
];
